import React from 'react';
import ScrollableTabPanel from 'flow/components/pbx/v2/detail-page/ScrollableTabPanel';
import { TicketKey } from 'flow/types/EntityKeys';

import ContactTabContent from './tabs/ContactTabContent';
import RelatedTabContent from './tabs/RelatedTabContent';
import TicketDetailsTabContent from './tabs/TicketDetailsTabContent';
import { TicketPageTabKey } from './TabsTicket';

interface Props {
    ticketKey: TicketKey;
    /** Any ticket tab except the conversation (which lives in the main pane). */
    tab: Exclude<TicketPageTabKey, 'CONVERSATION'>;
    /** Scroll-container id for the Related list's infinite scroll. */
    relatedScrollId: string;
    contentClassName?: string;
    /** True when hosted in the docked right panel (darkens the contact cards). */
    inPanel?: boolean;
}

/**
 * Renders the non-conversation ticket tabs (Contact / Related / Ticket
 * details) inside a ScrollableTabPanel. Shared by the main pane (details
 * panel closed), the docked details drawer and the overview popover so the
 * three render identically. Each tab gets its own panel (`key`), so a tab
 * opens at the top instead of inheriting the previous tab's scroll position.
 */
const TicketSideTabContent = ({
    ticketKey,
    tab,
    relatedScrollId,
    contentClassName = 'ps-4 pe-2 pt-4',
    inPanel = false,
}: Props) => {
    if (tab === 'CONTACT') {
        return (
            <ScrollableTabPanel key={tab} contentClassName={contentClassName}>
                <ContactTabContent ticketKey={ticketKey} inPanel={inPanel} />
            </ScrollableTabPanel>
        );
    }
    if (tab === 'RELATED') {
        return (
            <ScrollableTabPanel
                key={tab}
                id={relatedScrollId}
                contentClassName={contentClassName}
            >
                <RelatedTabContent
                    ticketKey={ticketKey}
                    scrollableTargetId={relatedScrollId}
                />
            </ScrollableTabPanel>
        );
    }
    return (
        <ScrollableTabPanel key={tab} contentClassName={contentClassName}>
            <TicketDetailsTabContent ticketKey={ticketKey} />
        </ScrollableTabPanel>
    );
};

export default React.memo(TicketSideTabContent);
